/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, mixins, fonts, Markup } from '@blend/consumer-components';

import { CommonAPI } from '../../../../../api/common/responses';
import { ConfirmationModal } from '../../../../../components/common/Modals/ConfirmationModal/ConfirmationModal';
import { PopoverMenu } from '../../../../../components/NotaryRoom/PopoverMenu/PopoverMenu';
import { useLocation } from '../../../../../hooks/useLocation';
import { User } from './User';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { isNotaryRoute } from '@utils/routing';
import { formatFullName, formatNames } from '@utils/formatting';
import { throttle } from '@utils/lodash';
import { hasCallStarted, hasVideoRecordingStarted } from '@utils/call';

export namespace UserGroup {
  export interface Props {
    usersStatus: keyof CommonAPI.UserDataResponse;
    userGroup: CommonAPI.Session;
    admitBrowser(sessionId: string): Promise<void>;
    removeBrowser(sessionId: string): Promise<void>;
    startCall?(): Promise<void>;
    startVideoRecording?(): Promise<void>;
    index: number;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.padding(12, 8, 4, 8),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    background: colors.white,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  groupWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  groupRoleWrapper: {
    color: colors.catalystLight40,
    fontWeight: fonts.weight.bold,
    textTransform: 'uppercase',
    fontSize: 10,
  },
  statusWrapper: {
    fontSize: 12,
    color: colors.clayPlus3,
    ...mixins.margin(0, 0, 8, 0),
  },
  admitButton: mixins.margin(8, 0, 8, 0),
  content: {
    fontSize: 16,
  },
  popoverMenu: {
    marginTop: 8,
  },
});

const RECORDING_DESCRIPTION =
  'Admitting a participant into the video call will begin recording for quality assurance and record-keeping purposes. The recording will stop after the appointment has ended.';

// displays all users information and status who are using the same device
// and show admit button if the browser sessions is in waiting room
export const UserGroup: React.FC<UserGroup.Props> = ({
  usersStatus,
  userGroup,
  admitBrowser,
  removeBrowser,
  startCall,
  startVideoRecording,
}) => {
  const { pathname } = useLocation();
  const isNotary = isNotaryRoute(pathname);
  const [isRecordingBeginModalOpen, setIsRecordingBeginModalOpen] = useState(false);
  const [isRemoveDeviceModalOpen, setIsRemoveDeviceModalOpen] = useState(false);
  const [isStartCallModalButtonDisabled, setIsStartCallModalButtonDisabled] = useState(false);
  const { id, callId, users, status, showAdmitButton, hidePopoverMenu } = userGroup;
  const canShowAdmitButton = isNotary && usersStatus === 'onboarding' && showAdmitButton;

  const primaryParticipants = users.filter(user => !user.primaryParticipantId);

  const userGroupRole = userGroup.isNotary ? 'Notary' : '';
  const userNames = primaryParticipants.map(user => `<strong>${formatFullName(user)}</strong>`);
  const formatedUserName = formatNames(userNames);
  const removeDeviceModalDescription = `${formatedUserName} will immediately be removed from the video call.<br/><br/>This action cannot be undone.`;

  const onAdmitClick = throttle(async () => {
    if (!(await hasVideoRecordingStarted(callId))) {
      openRecordingBeginModal();
    } else {
      await admitBrowser(id);
    }
  }, 2000);

  const openRecordingBeginModal = () => {
    setIsRecordingBeginModalOpen(true);
  };

  const closeRecordingBeginModal = () => {
    setIsRecordingBeginModalOpen(false);
  };

  /**
   * Starts the video recording and admits the borrower into the room
   * - Used when admitting the first borrower into the room
   * - Starts the call if the call has not started yet
   * - Always starts the video recording, because we only add video recording if there is a borrower in the room
   */
  const confirmStartRecordingAndAdmit = async () => {
    setIsStartCallModalButtonDisabled(true);
    const callStarted = await hasCallStarted(callId);
    if (!callStarted && !!startCall) {
      await startCall();
    }

    if (startVideoRecording) {
      await startVideoRecording();
    }

    await admitBrowser(id);
    setIsRecordingBeginModalOpen(false);
  };

  const openRemoveDeviceModal = () => {
    setIsRemoveDeviceModalOpen(true);
  };

  const closeRemoveDeviceModal = () => {
    setIsRemoveDeviceModalOpen(false);
  };

  const handleRemoveDeviceConfirm = async () => {
    await removeBrowser(id);
    setIsRemoveDeviceModalOpen(false);
  };

  const PopoverMenuItems = [{ name: 'RemoveDevice', display: 'Remove device', onClick: openRemoveDeviceModal }];

  return (
    <>
      <div className={css(styles.wrapper)} data-it="UserGroup">
        <div className={css(styles.groupRoleWrapper)}>{userGroupRole}</div>
        <div className={css(styles.groupWrapper)}>
          {primaryParticipants.map(user => (
            <User
              isNotary={isNotary}
              user={user}
              usersStatus={usersStatus}
              key={`${user.firstName} ${user.lastName}`}
            />
          ))}
        </div>
        <div className={css(styles.statusWrapper)}>{status}</div>
        {canShowAdmitButton && (
          <div className={css(styles.admitButton)}>
            <ActionButton
              data-dd-privacy="allow"
              sizing="xs"
              option="secondary"
              onClick={onAdmitClick}
              data-it={`AdmitButton-${id}`}
            >
              Admit
            </ActionButton>
          </div>
        )}
        <div className={css(styles.popoverMenu)}>
          {isNotary && !hidePopoverMenu && (
            <PopoverMenu data-it="Popover" iconType="more_horiz" menuItems={PopoverMenuItems} />
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={isRecordingBeginModalOpen}
        contentLabel="Recording will begin"
        handleClose={closeRecordingBeginModal}
        closeButtonText="Cancel"
        heading="Recording will begin"
        confirmButtonText="Continue"
        handleConfirm={confirmStartRecordingAndAdmit}
        description={RECORDING_DESCRIPTION}
        data-it="ConfirmationModal-Recording"
        isDisabled={isStartCallModalButtonDisabled}
      />
      <ConfirmationModal
        isOpen={isRemoveDeviceModalOpen}
        contentLabel="Do you want to remove this device?"
        handleClose={closeRemoveDeviceModal}
        closeButtonText="Cancel"
        heading="Do you want to remove this device?"
        confirmButtonText="Yes"
        handleConfirm={handleRemoveDeviceConfirm}
        data-it="ConfirmationModal-RemoveDevice"
      >
        <div data-it="RemoveDeviceModalContent" className={css(styles.content)}>
          <Markup content={removeDeviceModalDescription} />
        </div>
      </ConfirmationModal>
    </>
  );
};
